/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.addEventListener('change', this.handleVariantChange.bind(this));

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.updateAvailability();
        this.updateAddToCartButton();
      });
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMpn();
      this.updateMetafieldVisibility();
      VariantPicker.updateLabelText(evt);

      if (!this.preSelection) {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product
          }
        }));
      }
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll('[data-variant-metafield]').forEach((elem) => {
        elem.classList.toggle('hidden', elem.dataset.variantMetafield !== this.variant?.id.toString());
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;

      this.addBtn.disabled = !variantAvailable || this.preSelection;

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else {
        const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) variant = this.data.product.variants[i];
        }
      }

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update price.
        if (variant.compare_at_price > variant.price) {
          priceCurrentEl.querySelector('.js-label').textContent = theme.strings.salePrice;
          priceCurrentEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].price;
          if (priceWasEl) {
            priceWasEl.querySelector('.js-label').textContent = theme.strings.regularPrice;
            priceWasEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].compareAtPrice || '';
          }
        } else {
          priceCurrentEl.querySelector('.js-label').textContent = theme.strings.regularPrice;
          priceCurrentEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].price;
          if (priceWasEl) {
            priceWasEl.querySelector('.js-label').textContent = '';
            priceWasEl.querySelector('.js-value').innerHTML = '';
          }
        }

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the MPN.
     */
    updateMpn() {
      this.mpn = this.mpn || this.section.querySelector('.product-info__mpn-value');
      if (!this.mpn) return;

      const mpnAvailable = this.variant && this.data.formatted[this.variant.id]?.mpn;
      this.mpn.textContent = mpnAvailable ? this.data.formatted[this.variant.id].mpn : '';
      this.mpn.parentNode.hidden = !mpnAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selectedText = selector.querySelector('.custom-select__btn').textContent.trim();
          selectedOptions.push(selectedText === this.dataset.placeholderText ? null : selectedText);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
/**
 * Variant split: renders combined option values ("20mm x 1/2"", "16mm x 16mm x 16mm")
 * as separate native selects. The original (hidden) radio inputs stay the source of truth
 * for <variant-picker>; this element only translates between the selects and those radios.
 */
if (!customElements.get('variant-split')) {
  class VariantSplit extends HTMLElement {
    connectedCallback() {
      this.picker = this.closest('variant-picker');
      if (!this.picker) return;

      this.optionIndex = Number(this.dataset.optionIndex || 0);
      this.selects = Array.from(this.querySelectorAll('select'));
      this.radios = Array.from(
        this.picker.querySelectorAll(`input.js-option[name="${this.dataset.optionName}"]`)
      );
      this.combos = this.radios.map((radio) => ({
        radio,
        value: radio.value,
        parts: VariantSplit.splitValue(radio.value)
      }));

      this.selects.forEach((select) => {
        select.addEventListener('change', (evt) => {
          // Keep the select's change event away from <variant-picker>; it only understands .js-option inputs.
          evt.stopPropagation();
          this.handleSelectChange(select);
        });
      });

      this.picker.addEventListener('change', (evt) => {
        if (evt.target.matches && evt.target.matches('input.js-option') && this.radios.includes(evt.target)) {
          this.syncFromRadios();
        }
      });

      this.syncFromRadios();
    }

    /**
     * Normalises the separators used in combined values (" x ", " X ", " - ") to " x ".
     * @param {string} value - Combined option value.
     * @returns {string}
     */
    static normalise(value) {
      return String(value).trim().replace(/\s+[xX×]\s+|\s+-\s+/g, ' x ');
    }

    /**
     * Splits a combined value into its trimmed parts.
     * @param {string} value - Combined option value.
     * @returns {Array<string>}
     */
    static splitValue(value) {
      return VariantSplit.normalise(value).split(' x ').map((part) => part.trim());
    }

    /**
     * Product data from the picker's JSON block.
     * @returns {?object}
     */
    getProductData() {
      if (this.picker.data) return this.picker.data;
      const dataEl = this.picker.querySelector('[type="application/json"]');
      if (!dataEl) return null;
      this.picker.data = JSON.parse(dataEl.textContent);
      return this.picker.data;
    }

    /**
     * Currently selected option values in the picker (null when not selected), read from the DOM
     * so this works before <variant-picker> has been upgraded.
     * @returns {Array<?string>}
     */
    getPickerSelectedOptions() {
      const selectors = this.picker.querySelectorAll('.option-selector');
      return Array.from(selectors).map((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const btn = selector.querySelector('.custom-select__btn');
          const text = btn ? btn.textContent.trim() : '';
          return text === this.picker.dataset.placeholderText ? null : text;
        }
        const checked = selector.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    /**
     * Whether a combined value leads to a purchasable variant, given the other selected options.
     * @param {object} combo - Combo object.
     * @returns {{exists: boolean, available: boolean}}
     */
    getComboAvailability(combo) {
      const data = this.getProductData();
      if (!data) return { exists: true, available: true };
      const selected = this.getPickerSelectedOptions();
      const variants = data.product.variants.filter((variant) => (
        variant.options.every((opt, index) => {
          if (index === this.optionIndex) return opt === combo.value;
          return selected[index] == null || opt === selected[index];
        })
      ));
      return {
        exists: variants.length > 0,
        available: variants.some((variant) => variant.available)
      };
    }

    /**
     * Current parts as chosen in the selects ('' when a placeholder is selected).
     * @returns {Array<string>}
     */
    getSelectedParts() {
      return this.selects.map((select) => select.value);
    }

    /**
     * Finds the combos matching the given parts; a part of '' or undefined matches anything.
     * @param {Array<string>} parts - Parts to match.
     * @param {number} [upTo] - Only match parts with an index below this number.
     * @returns {Array<object>}
     */
    findCombos(parts, upTo = parts.length) {
      return this.combos.filter((combo) => (
        combo.parts.every((part, index) => index >= upTo || !parts[index] || part === parts[index])
      ));
    }

    /**
     * Handles a change on one of the selects.
     * @param {HTMLSelectElement} changed - The select that changed.
     */
    handleSelectChange(changed) {
      const changedIndex = this.selects.indexOf(changed);
      const parts = this.getSelectedParts();

      if (parts.some((part) => part === '')) {
        // Not every part chosen yet (placeholder): only refresh availability.
        this.updateAvailability();
        return;
      }

      let match = this.findCombos(parts)[0];

      if (!match) {
        // Keep the changed part and everything before it; pick the first existing combo for the rest.
        match = this.findCombos(parts, changedIndex + 1)[0];
      }

      if (!match) {
        // Should not happen (values come from existing combos); keep just the changed part.
        match = this.combos.find((combo) => combo.parts[changedIndex] === parts[changedIndex]);
      }

      if (!match) return;

      this.setSelects(match.parts);

      if (!match.radio.checked) {
        match.radio.click();
      } else {
        this.updateAvailability();
      }
    }

    /**
     * Syncs the selects with the currently checked radio (URL selection, first variant, quick add).
     */
    syncFromRadios() {
      const checked = this.combos.find((combo) => combo.radio.checked);
      if (checked) this.setSelects(checked.parts);
      this.updateAvailability();
    }

    /**
     * Sets the select values without triggering change events.
     * @param {Array<string>} parts - Parts to select.
     */
    setSelects(parts) {
      this.selects.forEach((select, index) => {
        if (parts[index] !== undefined && select.value !== parts[index]) {
          select.value = parts[index];
        }
      });
    }

    /**
     * Marks options per select: non-existent given the earlier parts → disabled;
     * existing but sold out → label with the "sold out" suffix.
     */
    updateAvailability() {
      const parts = this.getSelectedParts();
      const noStock = (window.theme && theme.strings && theme.strings.noStock) || '';

      this.selects.forEach((select, selectIndex) => {
        Array.from(select.options).forEach((option) => {
          if (option.value === '') return;
          const label = option.dataset.label || option.textContent;
          const candidate = parts.slice(0, selectIndex);
          candidate[selectIndex] = option.value;
          const combos = this.findCombos(candidate, selectIndex + 1);
          const exists = combos.length > 0;
          let available = false;

          if (exists) {
            available = combos.some((combo) => this.getComboAvailability(combo).available);
          }

          option.disabled = !exists;
          option.classList.toggle('is-unavailable', exists && !available);
          option.textContent = exists && !available && noStock ? `${label} – ${noStock}` : label;
        });
      });
    }
  }

  customElements.define('variant-split', VariantSplit);
}
